import { useEffect, useRef } from 'react';
import { showGlobalNotification } from './useNotifications';
import { audioManager } from '../utils/audioManager';
import { soundManager } from '../utils/soundManager';

export const useRealtimeEvents = (userRole: string) => {
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectingRef = useRef<boolean>(false);

  useEffect(() => {
    // Connect for all authenticated users
    if (!userRole) {
      console.log('[SSE] No user role, skipping SSE connection');
      return;
    }

    // Prevent multiple connections - check both ref and connecting state
    if (eventSourceRef.current && eventSourceRef.current.readyState !== EventSource.CLOSED) {
      console.log('[SSE] Already connected (readyState:', eventSourceRef.current.readyState, '), skipping');
      return;
    }

    // Prevent concurrent connection attempts
    if (isConnectingRef.current) {
      console.log('[SSE] Connection attempt already in progress, skipping');
      return;
    }

    const token = localStorage.getItem('auth_token');
    if (!token) {
      console.log('[SSE] No auth token found for SSE');
      return;
    }

    console.log('[SSE] Attempting SSE connection with token:', token.substring(0, 20) + '...');
    isConnectingRef.current = true;

    const eventSource = new EventSource(`${import.meta.env.VITE_API_URL}/realtime/events?token=${token}`);

    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[SSE] Message received:', data);
        
        switch (data.type) {
          case 'user_login':
            console.log('[SSE] Showing login toast:', data.message);
            showGlobalNotification.info('Kullanıcı Girişi', data.message, { duration: 3000 });
            break;
          case 'user_logout':
            console.log('[SSE] Showing logout toast:', data.message);
            showGlobalNotification.warning('Kullanıcı Çıkışı', data.message, { duration: 3000 });
            break;
          case 'new_message':
            console.log('New message via SSE:', data);
            window.dispatchEvent(new CustomEvent('refreshMessages'));
            soundManager.playMessageNotification();
            if (data.message?.sender_name) {
              showGlobalNotification.info(
                'Yeni Mesaj',
                `${data.message.sender_name}: ${data.message.message ? data.message.message.substring(0, 60) : ''}`,
                { duration: 5000 }
              );
            }
            break;
          case 'message_read':
            console.log('Message read via SSE:', data);
            // Trigger immediate unread count update
            window.dispatchEvent(new CustomEvent('messageRead', {
              detail: {
                messageId: data.messageId,
                readBy: data.readBy
              }
            }));
            // Also trigger message refresh but with lower priority
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('refreshMessages'));
            }, 100);
            break;
          case 'new_group_message':
            console.log('New group message via SSE:', data);
            // Dispatch group message event for sound notification
            window.dispatchEvent(new CustomEvent('newGroupMessage', {
              detail: {
                senderId: data.data.senderId,
                senderName: data.data.senderName,
                message: data.data.message
              }
            }));
            // Refresh group chat unread count
            window.dispatchEvent(new CustomEvent('refreshGroupUnreadCount'));
            // Show notification if not from current user
            showGlobalNotification.info(
              'Yeni Grup Mesajı',
              `${data.data.senderName}: ${data.data.message.message.substring(0, 50)}${data.data.message.message.length > 50 ? '...' : ''}`,
              { duration: 5000 }
            );
            break;
          case 'incoming_call':
            console.log('Incoming call via SSE:', data);
            // Dispatch incoming call event for CallNotification component
            window.dispatchEvent(new CustomEvent('incoming_call', {
              detail: {
                callId: data.callId,
                roomName: data.roomName,
                callType: data.callType,
                isGroupCall: data.isGroupCall,
                initiator: data.initiator
              }
            }));
            // Show notification
            showGlobalNotification.info(
              data.isGroupCall ? 'Grup Görüşmesi' : 'Gelen Arama',
              `${data.initiator.username} ${data.callType === 'video' ? 'görüntülü' : 'sesli'} arama başlattı`,
              { duration: 30000 }
            );
            break;
          case 'call_ended':
            console.log('Call ended via SSE:', data);
            window.dispatchEvent(new CustomEvent('call_ended', {
              detail: {
                callId: Number(data.callId),
                endedBy: data.endedBy
              }
            }));
            break;
          case 'call_rejected':
            console.log('Call rejected via SSE:', data);
            window.dispatchEvent(new CustomEvent('call_rejected', {
              detail: {
                callId: Number(data.callId),
                userId: data.userId,
                username: data.username
              }
            }));
            break;
          case 'call_accepted':
            console.log('Call accepted via SSE:', data);
            // Show notification
            showGlobalNotification.success(
              'Arama Kabul Edildi',
              `${data.username} aramayı kabul etti`,
              { duration: 3000 }
            );
            break;
          case 'participant_left':
            console.log('Participant left via SSE:', data);
            window.dispatchEvent(new CustomEvent('participant_left', {
              detail: {
                callId: Number(data.callId),
                userId: data.userId,
                username: data.username,
                isGroupCall: data.isGroupCall
              }
            }));
            showGlobalNotification.warning(
              'Katılımcı Ayrıldı',
              `${data.username} aramadan ayrıldı`,
              { duration: 3000 }
            );
            break;
          case 'connected':
            console.log('[SSE] Connected successfully');
            break;
          case 'heartbeat':
            console.log('[SSE] Heartbeat received');
            break;
        }
      } catch (error) {
        console.error('[SSE] Error parsing message:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('[SSE] Connection error:', error, 'readyState:', eventSource.readyState);
      isConnectingRef.current = false;

      // If connection is permanently closed, clean up
      if (eventSource.readyState === EventSource.CLOSED) {
        console.log('[SSE] Connection permanently closed, cleaning up');
        if (eventSourceRef.current === eventSource) {
          eventSourceRef.current = null;
        }
      }
      // EventSource will automatically reconnect for other states
    };

    eventSource.onopen = () => {
      console.log('[SSE] Connection opened successfully');
      isConnectingRef.current = false;
    };

    return () => {
      console.log('[SSE] Cleaning up SSE connection');
      isConnectingRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (eventSourceRef.current) {
        console.log('[SSE] Closing EventSource (readyState:', eventSourceRef.current.readyState, ')');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [userRole]);

  return null;
};